<!--
 This is the new guest checkout slide-in flow for the new checkout 2.0
 THis is temporary and will be replaced by a new Vue3 guest checkout slide-in, but for now both will exist in parallel until the new one is fully implemented in vue3 and ready to be switched on for all users
  -->
<template>
  <span>
    <oie-portal-wrapper :name="name">
      <template #title>
        <h2>{{ slideInTitle }}</h2>
      </template>

      <!-- ── Step: choice ── -->
      <div v-if="currentStep === 'choice'" class="guest-checkout-slide-in__choice">
        <p class="guest-checkout-slide-in__benefits-header">
          <strong>{{ tStatic('checkoutWizardBenefitsHeader') || 'Fördelar med ett eget konto:' }}</strong>
        </p>
        <ul class="guest-checkout-slide-in__benefits">
          <li>{{ tStatic('checkoutWizardBenefit1') || 'Se din orderhistorik och ladda ner PDF-fakturor' }}</li>
          <li>{{ tStatic('checkoutWizardBenefit2') || 'Möjlighet att spåra paket' }}</li>
          <li>{{ tStatic('checkoutWizardBenefit3') || 'Enkel hantering av retur och reklamation' }}</li>
          <li>{{ tStatic('checkoutWizardBenefit4') || 'Få åtkomst till "Mina Maskiner" och inköpslistor' }}</li>
        </ul>

        <oie-base-action
          size="large"
          data-pw="wizard-login-btn"
          @click="currentStep = 'login'">
          {{ tStatic('signIn') || 'Logga in' }}
        </oie-base-action>

        <oie-base-action
          size="large"
          data-pw="wizard-register-btn"
          @click="currentStep = 'register'">
          {{ tStatic('createAccountButtonLabel') || 'Skapa konto' }}
        </oie-base-action>

        <div class="guest-checkout-slide-in__divider">
          <span>{{ tStatic('orLabel') || 'eller' }}</span>
        </div>

        <oie-base-action
          theme="secondary"
          size="large"
          data-pw="wizard-guest-btn"
          @click="enterGuestStep">
          {{ tStatic('checkoutContinueAsGuest') || 'Fortsätt som gäst' }}
        </oie-base-action>

        <p class="guest-checkout-slide-in__guest-description">
          {{ tStatic('checkoutGuestDescription') || 'Fortsätt som gäst om du vill handla utan att skapa ett konto.' }}
        </p>
      </div>

      <!-- ── Step: login ── -->
      <div v-else-if="currentStep === 'login'" class="guest-checkout-slide-in__step">
        <a
          class="guest-checkout-slide-in__back"
          href="#"
          data-pw="wizard-back-from-login"
          @click.prevent="currentStep = 'choice'">
          {{ tStatic('checkoutWizardBack') || 'Tillbaka' }}
        </a>
        <oie-sign-in-box :return-url="clientModel.orderConfirmPageUrl"></oie-sign-in-box>
      </div>

      <!-- ── Step: register ── -->
      <div v-else-if="currentStep === 'register'" class="guest-checkout-slide-in__step">
        <a
          class="guest-checkout-slide-in__back"
          href="#"
          data-pw="wizard-back-from-register"
          @click.prevent="currentStep = 'choice'">
          {{ tStatic('checkoutWizardBack') || 'Tillbaka' }}
        </a>

        <validation-observer ref="registerObserver" tag="div">
          <oie-form-wrapper class="input-column__container" @submit="submitRegister">
            <fieldset
              class="input-column__container input-column__container--register"
              role="radiogroup">
              <legend class="sr-only">
                {{ tStatic('selectCustomerTypeAria') }}
              </legend>
              <oie-base-radio-checkbox
                v-model="reg.selectedCustomerType"
                value="company"
                name="reg-customer-type"
                data-pw="reg-customer-type-company"
                :label="tStatic('signUpCompanyLabel') || 'Företag'"></oie-base-radio-checkbox>
              <oie-base-radio-checkbox
                v-model="reg.selectedCustomerType"
                value="private"
                name="reg-customer-type"
                data-pw="reg-customer-type-private"
                :label="tStatic('signUpPrivateLabel') || 'Privatperson'"></oie-base-radio-checkbox>
            </fieldset>

            <template v-if="reg.selectedCustomerType">
              <validation-provider
                v-if="useCountryDropDown && (hasMultipleMarkets || clientModel.impersonating)"
                v-slot="{ errors }"
                rules="required"
                slim>
                <oie-base-dropdown
                  v-model="reg.address.countryCode"
                  :is-disabled="!hasMultipleMarkets"
                  :label-text="`${tStatic('addressCountryLabel')}*`"
                  :options="clientModel.countryList"
                  :default-selected="createDefaultSelection"
                  :initial-text="createInitialText"
                  :use-filter-functionality="clientModel.countryList.length > 10"
                  :filter-input-placeholder="tStatic('addressCountryPlaceholder')"
                  :error-messages="errors"
                  name="guest-register-country"
                  container-classes="order-request__dropdown"
                  z-index="10"
                  data-pw="reg-country-dropdown"
                  @input="setActiveCountry">
                </oie-base-dropdown>
              </validation-provider>

              <validation-provider
                v-if="!useCountryDropDown && (hasMultipleMarkets || clientModel.impersonating)"
                v-slot="{ errors }"
                rules="required">
                <div>
                  <fieldset class="input-column__container input-column__container--register" role="radiogroup">
                    <legend class="body-smaller-semi-bold">
                      {{ tStatic('addressCountryLabel') }}
                    </legend>
                    <oie-base-radio-checkbox
                      v-for="country in clientModel.countryList"
                      :key="'reg-country-' + country.value"
                      v-model="reg.address.countryCode"
                      name="guest-register-country"
                      :value="country.value"
                      :label="country.text"
                      @input="setActiveCountry"></oie-base-radio-checkbox>
                  </fieldset>
                  <div v-if="errors.length" class="guest-checkout-slide-in__error">
                    {{ errors[0] }}
                  </div>
                </div>
              </validation-provider>

              <div class="input-column__container">
                <h3 class="input-column__header guest-checkout-slide-in__section-header">
                  {{ tStatic('customerInformationLabel') || 'Kunduppgifter' }}
                </h3>

                <validation-provider
                  v-if="regIsCompany"
                  v-slot="{ errors }"
                  rules="required|invalidchars"
                  slim>
                  <oie-base-input-v2
                    v-model="reg.companyName"
                    required
                    data-pw="reg-company-name"
                    :label="tStatic('companyNameLabel') || 'Företagsnamn'"
                    :placeholder="tStatic('companyNamePlaceholder') || 'Ange företagsnamn'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|invalidchars|firstNameNoPN"
                  slim>
                  <oie-base-input-v2
                    v-model="reg.firstName"
                    required
                    maxlength="49"
                    data-pw="reg-first-name"
                    :label="tStatic('firstNameLabel') || 'Förnamn'"
                    :placeholder="tStatic('firstNamePlaceholder') || 'Ange förnamn'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|invalidchars|lastNameNoPN"
                  slim>
                  <oie-base-input-v2
                    v-model="reg.lastName"
                    required
                    maxlength="50"
                    data-pw="reg-last-name"
                    :label="tStatic('lastNameLabel') || 'Efternamn'"
                    :placeholder="tStatic('lastNamePlaceholder') || 'Ange efternamn'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|mobile:^[0-9+]+$"
                  slim>
                  <oie-phone-input
                    v-model="reg.phone"
                    :label="tStatic('phoneLabel') || 'Mobilnummer'"
                    :placeholder="tStatic('phonePlaceholderLabel') || 'Ange mobilnummer'"
                    maxlength="27"
                    required
                    data-pw="reg-phone"
                    :errors="errors"
                    :country-code="selectedActiveCountry ? selectedActiveCountry.callingCode : null"></oie-phone-input>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|email|invalidEmailCharacters"
                  slim>
                  <oie-base-input-v2
                    v-model="reg.email"
                    required
                    maxlength="80"
                    data-pw="reg-email"
                    :label="tStatic('email') || 'E-post'"
                    :placeholder="tStatic('emailPlaceholder') || 'Ange e-post'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>
              </div>

              <div class="input-column__container">
                <h3 class="input-column__header guest-checkout-slide-in__section-header">
                  {{ tStatic('addressHeaderLabel') || 'Adress' }}
                </h3>
                <oie-address-component
                  id="reg-address"
                  :address="reg.address"
                  :use-country-dropdown="false"></oie-address-component>
              </div>

              <div class="input-column__container">
                <validation-provider
                  v-slot="{ errors }"
                  vid="reg-pw"
                  rules="required|passwordPolicy"
                  slim>
                  <oie-base-input-v2
                    v-model="reg.password"
                    type="password"
                    required
                    data-pw="reg-password"
                    :label="tStatic('passwordLabel') || 'Lösenord'"
                    :placeholder="tStatic('passwordPlaceholder') || 'Ange lösenord'"
                    :description="tStatic('passwordPolicy')"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|password:reg-pw"
                  slim>
                  <oie-base-input-v2
                    v-model="reg.passwordRepeat"
                    type="password"
                    required
                    data-pw="reg-password-repeat"
                    :label="tStatic('repeatPasswordLabel') || 'Upprepa lösenord'"
                    :placeholder="tStatic('repeatPasswordPlaceholder') || 'Upprepa lösenord'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider v-slot="{ errors }" rules="checked" slim>
                  <oie-base-radio-checkbox
                    v-model="reg.acceptTerms"
                    required
                    data-pw="reg-accept-terms"
                    :errors="errors"
                    :label="tStatic('approveTerms') || 'Jag godkänner villkoren'"
                    :description="tStatic('approveTermsLabel')"></oie-base-radio-checkbox>
                </validation-provider>
              </div>

              <div v-if="reg.submitError" class="guest-checkout-slide-in__error">
                {{ reg.submitError }}
              </div>

              <oie-base-action
                type="submit"
                size="large"
                data-pw="reg-submit"
                :disabled="reg.isSubmitting">
                {{ tStatic('createAccountButtonLabel') || 'Skapa konto' }}
              </oie-base-action>
            </template>
          </oie-form-wrapper>
        </validation-observer>
      </div>

      <!-- ── Step: guest ── -->
      <div v-else-if="currentStep === 'guest'" class="guest-checkout-slide-in__step">
        <a
          class="guest-checkout-slide-in__back"
          href="#"
          data-pw="wizard-back-from-guest"
          @click.prevent="currentStep = 'choice'">
          {{ tStatic('checkoutWizardBack') || 'Tillbaka' }}
        </a>

        <validation-observer ref="guestObserver" tag="div">
          <div v-if="isPrefilling" class="guest-checkout-slide-in__loader">
            <div class="guest-checkout-slide-in__spinner"></div>
          </div>
          <oie-form-wrapper v-else class="input-column__container" @submit="submitGuestSetup">
            <fieldset
              class="input-column__container input-column__container--register"
              role="radiogroup">
              <legend class="sr-only">
                {{ tStatic('selectCustomerTypeAria') }}
              </legend>
              <oie-base-radio-checkbox
                v-model="selectedCustomerType"
                value="company"
                name="guest-customer-type"
                data-pw="guest-customer-type-company"
                :label="tStatic('signUpCompanyLabel') || 'Företag'"></oie-base-radio-checkbox>
              <oie-base-radio-checkbox
                v-model="selectedCustomerType"
                value="private"
                name="guest-customer-type"
                data-pw="guest-customer-type-private"
                :label="tStatic('signUpPrivateLabel') || 'Privatperson'"></oie-base-radio-checkbox>
            </fieldset>

            <template v-if="selectedCustomerType">
              <validation-provider
                v-if="useCountryDropDown && (hasMultipleMarkets || clientModel.impersonating)"
                v-slot="{ errors }"
                rules="required"
                slim>
                <oie-base-dropdown
                  v-model="form.address.countryCode"
                  :is-disabled="!hasMultipleMarkets"
                  :label-text="`${tStatic('addressCountryLabel')}*`"
                  :options="clientModel.countryList"
                  :default-selected="createDefaultSelection"
                  :initial-text="createInitialText"
                  :use-filter-functionality="clientModel.countryList.length > 10"
                  :filter-input-placeholder="tStatic('addressCountryPlaceholder')"
                  :error-messages="errors"
                  name="guest-checkout-country"
                  container-classes="order-request__dropdown"
                  z-index="10"
                  data-pw="guest-country-dropdown"
                  @input="setActiveCountry">
                </oie-base-dropdown>
              </validation-provider>

              <validation-provider
                v-if="!useCountryDropDown && (hasMultipleMarkets || clientModel.impersonating)"
                v-slot="{ errors }"
                rules="required">
                <div>
                  <fieldset class="input-column__container input-column__container--register" role="radiogroup">
                    <legend class="body-smaller-semi-bold">
                      {{ tStatic('addressCountryLabel') }}
                    </legend>
                    <oie-base-radio-checkbox
                      v-for="country in clientModel.countryList"
                      :key="'guest-country-' + country.value"
                      v-model="form.address.countryCode"
                      name="guest-checkout-country"
                      :value="country.value"
                      :label="country.text"
                      @input="setActiveCountry"></oie-base-radio-checkbox>
                  </fieldset>
                  <div v-if="errors.length" class="guest-checkout-slide-in__error">
                    {{ errors[0] }}
                  </div>
                </div>
              </validation-provider>

              <div class="input-column__container">
                <h3 class="input-column__header guest-checkout-slide-in__section-header">
                  {{ tStatic('customerInformationLabel') || 'Kunduppgifter' }}
                </h3>

                <validation-provider
                  v-if="isCompany"
                  v-slot="{ errors }"
                  rules="required|invalidchars"
                  slim>
                  <oie-base-input-v2
                    v-model="form.companyName"
                    required
                    data-pw="guest-company-name"
                    :label="tStatic('companyNameLabel') || 'Företagsnamn'"
                    :placeholder="tStatic('companyNamePlaceholder') || 'Ange företagsnamn'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider
                  v-if="isCompany && selectedActiveCountry && selectedActiveCountry.useVatCode"
                  ref="vatNumberProvider"
                  v-slot="{ errors }"
                  :debounce="500"
                  mode="lazy"
                  :rules="createVatNumberValidateRule"
                  slim>
                  <oie-vat-input
                    v-model="form.vatNumber"
                    :label="vatNumberLabel"
                    :placeholder="tStatic('companyVATPlaceholder') || 'Ange momsnummer'"
                    :errors="errors"
                    :country-code="selectedActiveCountry ? selectedActiveCountry.value.replace(/^GR/, 'EL') : null"
                    data-pw="guest-vat-number"
                    maxlength="18"
                    @blur="$refs.vatNumberProvider.validate()"></oie-vat-input>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|invalidchars|firstNameNoPN"
                  slim>
                  <oie-base-input-v2
                    v-model="form.firstName"
                    required
                    maxlength="49"
                    data-pw="guest-first-name"
                    :label="tStatic('firstNameLabel') || 'Förnamn'"
                    :placeholder="tStatic('firstNamePlaceholder') || 'Ange förnamn'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|invalidchars|lastNameNoPN"
                  slim>
                  <oie-base-input-v2
                    v-model="form.lastName"
                    required
                    maxlength="50"
                    data-pw="guest-last-name"
                    :label="tStatic('lastNameLabel') || 'Efternamn'"
                    :placeholder="tStatic('lastNamePlaceholder') || 'Ange efternamn'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|mobile:^[0-9+]+$"
                  slim>
                  <oie-phone-input
                    v-model="form.phone"
                    :label="tStatic('phoneLabel') || 'Mobilnummer'"
                    :placeholder="tStatic('phonePlaceholderLabel') || 'Ange mobilnummer'"
                    maxlength="27"
                    required
                    data-pw="guest-phone"
                    :errors="errors"
                    :country-code="selectedActiveCountry ? selectedActiveCountry.callingCode : null"></oie-phone-input>
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|email|invalidEmailCharacters"
                  slim>
                  <oie-base-input-v2
                    v-model="form.email"
                    required
                    maxlength="80"
                    data-pw="guest-email"
                    :label="tStatic('email') || 'E-post'"
                    :placeholder="tStatic('emailPlaceholder') || 'Ange e-post'"
                    :errors="errors"></oie-base-input-v2>
                </validation-provider>
              </div>

              <div class="input-column__container">
                <h3 class="input-column__header guest-checkout-slide-in__section-header">
                  {{ tStatic('addressHeaderLabel') || 'Adress' }}
                </h3>
                <oie-address-component
                  id="guest-checkout-address"
                  :address="form.address"
                  :use-country-dropdown="false"></oie-address-component>
              </div>

              <div v-if="submitError" class="guest-checkout-slide-in__error">
                {{ submitError }}
              </div>

              <oie-base-action
                type="submit"
                size="large"
                data-pw="guest-checkout-submit"
                :disabled="isSubmitting">
                {{ tStatic('cartGotoCheckout') || 'Gå till kassan' }}
              </oie-base-action>
            </template>
          </oie-form-wrapper>
        </validation-observer>
      </div>
    </oie-portal-wrapper>
  </span>
</template>

<script>
import { mapActions } from 'vuex'
import countryHandling from '@/mixins/country-handling'
import httpService from '@/core/http-service'
import { extend } from 'vee-validate'

export default {
  mixins: [countryHandling],
  data() {
    return {
      name: 'guest-checkout-slide-in',
      currentStep: 'choice',

      // Guest form state
      selectedCustomerType: null,
      isSubmitting: false,
      isPrefilling: false,
      submitError: null,
      form: {
        firstName: '',
        lastName: '',
        companyName: '',
        vatNumber: '',
        email: '',
        phone: '',
        address: {
          addressLine1: null,
          addressCo: null,
          postalCode: null,
          city: null,
          countryCode: null
        }
      },

      // Register form state
      reg: {
        selectedCustomerType: null,
        companyName: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        password: '',
        passwordRepeat: '',
        acceptTerms: false,
        isSubmitting: false,
        submitError: null,
        address: {
          addressLine1: null,
          addressCo: null,
          postalCode: null,
          city: null,
          countryCode: null
        }
      }
    }
  },
  computed: {
    slideInTitle() {
      switch (this.currentStep) {
        case 'login': return this.tStatic('signIn') || 'Logga in'
        case 'register': return this.tStatic('createAccountButtonLabel') || 'Skapa konto'
        case 'guest': return this.tStatic('checkoutContinueAsGuest') || 'Fortsätt som gäst'
        default: return this.tStatic('checkoutWizardChoiceTitle') || 'Välj hur du vill fortsätta'
      }
    },
    isCompany() {
      return this.selectedCustomerType === 'company'
    },
    regIsCompany() {
      return this.reg.selectedCustomerType === 'company'
    },
    createVatNumberValidateRule() {
      const countryCode = this.selectedActiveCountry
        ? this.selectedActiveCountry.value
        : this.form.address.countryCode
      return countryCode ? `vatNumber:${countryCode.replace(/^GR/, 'EL')}` : null
    },
    vatNumberLabel() {
      if (this.clientModel.currentMarket === 'se' || this.clientModel.currentMarket === 'no') {
        return this.tStatic('companyOrgNrLabel') || 'Org.nummer'
      }
      return this.tStatic('companyVATLabel') || 'VAT-nummer'
    },
    apiBasePath() {
      return `/api/${this.clientModel.currentMarket}/${this.clientModel.currentLanguage}`
    }
  },
  watch: {
    'form.address.countryCode'(value) {
      this.setActiveCountry(value)
    },
    'reg.address.countryCode'(value) {
      this.setActiveCountry(value)
    }
  },
  beforeMount() {
    this.setActiveCountry(this.defaultCountry ? this.defaultCountry.value : null)
  },
  created() {
    extend('firstNameNoPN', {
      validate: value => {
        if (!value) return false
        const hasLetter = /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(value)
        if (!hasLetter) return false
        const pnPattern = /^\d{10,12}$/
        if (pnPattern.test(value)) return false
        return true
      },
      message: this.tStatic('firstNameNoPNValidationMessage')
    })

    extend('lastNameNoPN', {
      validate: value => {
        if (!value) return false
        const hasLetter = /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(value)
        if (!hasLetter) return false
        const pnPattern = /^\d{10,12}$/
        if (pnPattern.test(value)) return false
        return true
      },
      message: this.tStatic('lastNameNoPNValidationMessage')
    })

    extend('invalidEmailCharacters', {
      validate: value => {
        if (!value) return false
        const hasInvalidLetter = /^(?!.*[À-ÖØ-öø-ÿ]).+$/.test(value)
        if (!hasInvalidLetter) return false
        return true
      },
      message: this.tStatic('validateEmailCharacters')
    })
  },
  methods: {
    ...mapActions('ui', ['setActiveSlideIn']),
    open() {
      this.currentStep = 'choice'
      this.setActiveSlideIn(this.name)
    },
    enterGuestStep() {
      this.currentStep = 'guest'
      this.fetchAndPrefill()
    },
    async fetchAndPrefill() {
      this.isPrefilling = true
      try {
        const response = await httpService.xhr('get', `${this.apiBasePath}/checkout/state`, { useDeviationEndpoint: true })
        const state = response.data

        if (state.isCompanyUser) {
          this.selectedCustomerType = 'company'
        } else if (state.deliveryAddress && state.deliveryAddress.firstName) {
          this.selectedCustomerType = 'private'
        }

        if (state.notificationEmail) this.form.email = state.notificationEmail
        if (state.vatNumber) this.form.vatNumber = state.vatNumber

        if (state.deliveryAddress) {
          const addr = state.deliveryAddress
          if (addr.firstName) this.form.firstName = addr.firstName
          if (addr.lastName) this.form.lastName = addr.lastName
          if (addr.companyName) this.form.companyName = addr.companyName
          if (addr.phone) this.form.phone = addr.phone
          if (addr.addressLine1) this.form.address.addressLine1 = addr.addressLine1
          if (addr.addressCo) this.form.address.addressCo = addr.addressCo
          if (addr.postalCode) this.form.address.postalCode = addr.postalCode
          if (addr.city) this.form.address.city = addr.city
          if (addr.countryCode) this.form.address.countryCode = addr.countryCode
        }
      } catch {
        // Prefill is best-effort, ignore errors
      } finally {
        this.isPrefilling = false
      }
    },
    async submitGuestSetup() {
      const isValid = await this.$refs.guestObserver.validate()
      if (!isValid) return

      this.isSubmitting = true
      this.submitError = null

      const payload = {
        isCompanyCustomer: this.isCompany,
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        companyName: this.form.companyName,
        vatNumber: this.form.vatNumber,
        email: this.form.email,
        phone: this.form.phone,
        addressLine1: this.form.address.addressLine1,
        addressCo: this.form.address.addressCo,
        postalCode: this.form.address.postalCode,
        city: this.form.address.city,
        countryCode: this.form.address.countryCode || (this.selectedActiveCountry ? this.selectedActiveCountry.value : null)
      }

      try {
        await httpService.xhr('post', `${this.apiBasePath}/checkout/guest-setup`, { useDeviationEndpoint: true, data: payload })
        window.location.href = this.clientModel.orderConfirmPageUrl
      } catch (error) {
        this.submitError = this.tStatic('genericErrorMessage') || 'Något gick fel. Försök igen.'
        this.isSubmitting = false
      }
    },
    async submitRegister() {
      const isValid = await this.$refs.registerObserver.validate()
      if (!isValid) return

      this.reg.isSubmitting = true
      this.reg.submitError = null

      const requestData = {
        isCompanyCustomer: this.regIsCompany,
        customerName: this.regIsCompany ? this.reg.companyName : null,
        firstName: this.reg.firstName,
        lastName: this.reg.lastName,
        phone: this.reg.phone,
        username: this.reg.email,
        password: this.reg.password,
        addressLine1: this.reg.address.addressLine1,
        addressCo: this.reg.address.addressCo,
        postalCode: this.reg.address.postalCode,
        city: this.reg.address.city,
        countryCode: this.reg.address.countryCode || (this.selectedActiveCountry ? this.selectedActiveCountry.value : null)
      }

      try {
        await httpService.post('auth/create', requestData)
        window.location.href = this.clientModel.orderConfirmPageUrl
      } catch (error) {
        this.reg.submitError = this.tStatic('genericErrorMessage') || 'Något gick fel. Försök igen.'
        this.reg.isSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.guest-checkout-slide-in__choice > * + * {
  margin-top: measure(half);
}

.guest-checkout-slide-in__benefits-header {
  margin-bottom: measure(quarter);
}

.guest-checkout-slide-in__benefits {
  padding-left: measure(full);
  margin-bottom: measure(half);

  li {
    list-style: disc;
    margin-bottom: measure(quarter);
  }
}

.guest-checkout-slide-in__divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: color(gray-2);
  margin: measure(quarter) 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid color(gray-4);
  }

  span {
    padding: 0 measure(half);
  }
}

.guest-checkout-slide-in__guest-description {
  color: color(gray-2);
  margin-top: measure(quarter);
}

.guest-checkout-slide-in__back {
  display: inline-block;
  margin-bottom: measure(full);
  color: color(text-blue);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.guest-checkout-slide-in__section-header {
  color: color(text-blue);
}

.guest-checkout-slide-in__error {
  color: color(red);
  margin-bottom: measure(half);
}

.guest-checkout-slide-in__loader {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.guest-checkout-slide-in__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid color(gray-4);
  border-top-color: color(primary);
  border-radius: 50%;
  animation: guest-checkout-spin 0.7s linear infinite;
}

@keyframes guest-checkout-spin {
  to { transform: rotate(360deg); }
}
</style>
